import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Image, Linking } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { PublicNav } from "../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";

const AKC_FASTCAT_URL = "https://www.akc.org/sports/coursing/fast-cat/";
const APP_STORE_URL = "https://apps.apple.com/us/app/dash-hound/id6762554245";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.dashhound.app";

// Cornerstone explainer + hub. Links out to rankings, events, join, calculator.
export default function WhatIsFastCat() {
  const router = useRouter();

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>What Is Fast CAT? The 100-Yard Dash Dog Sport, Explained — Dash-Hound</title>
          <meta name="description" content="Fast CAT is AKC's timed 100-yard dash where dogs chase a lure one at a time. Learn how it works, which dogs can run, how speed and points are scored, the titles you can earn, and how to find an event near you." />
          <meta property="og:title" content="What Is Fast CAT? The 100-Yard Dash Dog Sport, Explained" />
          <meta property="og:description" content="How Fast CAT works, who can run, how it's scored, the titles, and how to find an event near you." />
          <meta property="og:image" content="https://dashhound.app/pages/what-is-fast-cat.jpg" />
          <meta property="og:url" content="https://dashhound.app/what-is-fast-cat" />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="What Is Fast CAT? The 100-Yard Dash Dog Sport, Explained" />
          <meta name="twitter:description" content="How Fast CAT works, who can run, how it's scored, the titles, and how to find an event near you." />
          <meta name="twitter:image" content="https://dashhound.app/pages/what-is-fast-cat.jpg" />
        </Head>
      )}

      <PublicNav />

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroKicker}>THE SPORT</Text>
          <Text style={styles.heroTitle}>What Is Fast CAT?</Text>
          <Text style={styles.heroSub}>
            AKC's fastest sport: a timed 100-yard dash where dogs chase a lure, one at a time. Here's how it works — and how to get your dog on the line.
          </Text>
        </View>

        <View style={styles.heroImgWrap}>
          {Platform.OS === "web" ? (
            // @ts-ignore raw img on web
            <img src="/pages/what-is-fast-cat.jpg" alt="A blue heeler airborne at full sprint during a Fast CAT run" style={{ width: "100%", height: "auto", display: "block" } as any} />
          ) : (
            <Image source={{ uri: "https://dashhound.app/pages/what-is-fast-cat.jpg" }} style={{ width: "100%", aspectRatio: 3 / 2 }} resizeMode="cover" />
          )}
        </View>

        <View style={styles.body}>
          <Text style={styles.lead}>
            Fast CAT — short for <Strong>Coursing Ability Test</Strong> — is exactly what it looks like: your dog sprints 100 yards in a straight line, alone, chasing a plastic-bag lure pulled along the ground. One dog runs at a time, they're electronically timed, and the whole thing is over in about four to seven seconds. No obedience, no jumps, no training resume required — just drive and a love of running.
          </Text>

          <H2>How a run works</H2>
          <Bullet>You hand your dog to a <Strong>catcher</Strong> at the far (finish) end, then walk to the start line.</Bullet>
          <Bullet>On your signal, a <Strong>lure operator</Strong> starts the bag moving and you release your dog to chase it.</Bullet>
          <Bullet>Your dog runs the straight, fenced 100-yard course to the catcher, who's holding their favorite toy or treat.</Bullet>
          <Bullet>The timer records the run, and that time is converted into a <Strong>speed (MPH)</Strong> and <Strong>points</Strong>.</Bullet>

          <H2>Which dogs can run?</H2>
          <Text style={styles.p}>
            Almost any of them. Fast CAT is open to <Strong>all AKC breeds and all-American (mixed-breed) dogs</Strong> — you'll see greyhounds and whippets next to corgis, pugs, poodles, and shelter mixes. The main requirements: your dog must be at least <Strong>12 months old</Strong>, healthy and sound, and interested in chasing. A short handicap system (more below) keeps it fair across sizes, so small dogs genuinely compete.
          </Text>

          <H2>How dogs are scored</H2>
          <Text style={styles.p}>
            Speed is calculated from the 100-yard time, then multiplied by a height handicap (dogs under 18″ get a bonus multiplier so they aren't buried by the tall breeds). That gives each run a point value. We break the full math down — with worked examples — in{" "}
            <A onPress={() => router.push("/(auth)/blog/how-to-calculate-fast-cat-points/" as any)}>How to Calculate Fast CAT Points</A>, and you can crunch your own dog's numbers with our free{" "}
            <A onPress={() => router.push("/(auth)/tools/" as any)}>Fast CAT calculators</A>.
          </Text>

          <H2>Titles you can earn</H2>
          <Text style={styles.p}>
            Points accumulate over your dog's career and unlock AKC suffix titles — <Strong>BCAT</Strong> at 150 points, <Strong>DCAT</Strong> at 500, <Strong>FCAT</Strong> at 1,000, and FCAT2, FCAT3, and beyond every 500 after that. Curious how your dog stacks up? See where breeds rank by speed on our{" "}
            <A onPress={() => router.push("/(auth)/fast-cat-rankings/" as any)}>Fast CAT Rankings</A>.
          </Text>

          <H2>How to get started</H2>
          <Text style={styles.p}>
            First, register your dog with AKC (purebreds use their registration; mixed breeds enroll in the free <Strong>AKC Canine Partners</Strong> program). Then find a nearby event and enter. Our{" "}
            <A onPress={() => router.push("/(auth)/how-to-signup/" as any)}>step-by-step sign-up guide</A>{" "}
            walks you through it, and you can search{" "}
            <A onPress={() => router.push("/(auth)/fast-cat-events/" as any)}>Fast CAT events near you</A>{" "}
            right on this site. Want the official rulebook? It's on the{" "}
            <A onPress={() => Linking.openURL(AKC_FASTCAT_URL)}>AKC Fast CAT page</A>.
          </Text>

          <View style={styles.cta}>
            <Text style={styles.ctaTitle}>Find a Fast CAT event near you</Text>
            <Text style={styles.ctaText}>
              Search upcoming events by location — no account needed — then track every run, speed, and title in the Dash-Hound app.
            </Text>
            <View style={{ flexDirection: "row", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <TouchableOpacity style={styles.ctaPrimary} onPress={() => router.push("/(auth)/fast-cat-events/" as any)}>
                <Text style={styles.ctaPrimaryText}>Find Events →</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ctaSecondary} onPress={() => Linking.openURL(APP_STORE_URL)}>
                <Text style={styles.ctaSecondaryText}> iOS App</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ctaSecondary} onPress={() => Linking.openURL(PLAY_STORE_URL)}>
                <Text style={styles.ctaSecondaryText}>▶ Android App</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

function H2({ children }: { children: string }) {
  return <Text style={styles.h2}>{children}</Text>;
}
function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.bulletRow}>
      <Text style={styles.bulletDot}>•</Text>
      <Text style={styles.bulletText}>{children}</Text>
    </View>
  );
}
function Strong({ children }: { children: React.ReactNode }) {
  return <Text style={{ fontWeight: "700", color: NAVY }}>{children}</Text>;
}
function A({ children, onPress }: { children: React.ReactNode; onPress: () => void }) {
  return <Text style={styles.link} onPress={onPress}>{children}</Text>;
}

const styles = StyleSheet.create({
  content: { flexGrow: 1 },
  hero: { backgroundColor: NAVY, paddingHorizontal: 24, paddingTop: 40, paddingBottom: 36, alignItems: "center" },
  heroKicker: { color: ORANGE, fontSize: 12, fontWeight: "800", letterSpacing: 1.5, marginBottom: 10 },
  heroTitle: { color: WHITE, fontSize: 38, fontWeight: "900", textAlign: "center", letterSpacing: -0.5 },
  heroSub: { color: "rgba(255,255,255,0.75)", fontSize: 17, textAlign: "center", marginTop: 14, maxWidth: 640, lineHeight: 26 },
  heroImgWrap: { width: "100%", maxWidth: 960, alignSelf: "center", backgroundColor: "#F3F4F6" },
  body: { maxWidth: 720, alignSelf: "center", width: "100%", paddingHorizontal: 20, paddingVertical: 32 },
  lead: { fontSize: 17, color: TEXT, lineHeight: 28, marginBottom: 8, fontWeight: "500" },
  h2: { fontSize: 22, fontWeight: "800", color: NAVY, marginTop: 32, marginBottom: 12 },
  p: { fontSize: 16, color: TEXT, lineHeight: 26, marginBottom: 12 },
  link: { color: "#B45309", fontWeight: "700", textDecorationLine: "underline" },
  bulletRow: { flexDirection: "row", marginBottom: 8, paddingLeft: 4 },
  bulletDot: { fontSize: 16, color: ORANGE, marginRight: 10, lineHeight: 26, fontWeight: "700" },
  bulletText: { flex: 1, fontSize: 16, color: TEXT, lineHeight: 26 },
  cta: { marginTop: 40, padding: 22, backgroundColor: "#F9FAFB", borderRadius: 14, borderWidth: 1, borderColor: "#E5E7EB" },
  ctaTitle: { fontSize: 20, fontWeight: "800", color: NAVY, marginBottom: 8 },
  ctaText: { fontSize: 15, color: TEXT, lineHeight: 22 },
  ctaPrimary: { backgroundColor: NAVY, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaPrimaryText: { color: WHITE, fontWeight: "700", fontSize: 14 },
  ctaSecondary: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaSecondaryText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center" },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
